import React from 'react';
import { Box, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import {
  DATE_UNAVAILABILITY_REASON,
  dateAvailabilityStatusPropType,
  dateToIsoKey,
} from '../../../../utils/availabilityStatus';
import { CalendarDayButton, CalendarDayRoot } from './calendarDayStyles';
import { getDisabledDayTooltip } from './calendarDayMeta';

/**
 * Presentational calendar day.
 * Styles are static (`CalendarDayRoot` / `CalendarDayButton` (./calendarDayStyles)) and driven by CSS classes
 * (`selected`, `inRange`, `disabledReason-*`). The optional `sx` prop (`{ root, day }`) is only for
 * rare per-cell overrides.
 *
 * @param {object} props
 * @param {Date} props.day
 * @param {object} props.pickersDayProps - props from MUI CalendarPicker `renderDay`
 * @param {boolean} props.selected - selected check-in or check-out (including hovered checkout candidate)
 * @param {boolean} props.inRange - between selected/hovered check-in and check-out, inclusive
 * @param {boolean} props.disabled - whether the day should be selectable
 * @param {string|null} [props.disabledReason] - `DATE_UNAVAILABILITY_REASON`; drives tooltip/style when disabled
 * @param {{ root?: object, day?: object }} [props.sx] - per-cell style overrides on top of the static styles
 * @param {(day: Date) => void} props.onSelect
 * @param {(day: Date) => void} props.onMouseOver
 * @param {(event: object, day: Date) => void} [props.onFocus]
 * @param {(event: object, day: Date) => boolean|void} [props.onKeyDown] - return true if handled (skip MUI)
 * @param {object|null} [props.availabilityStatus] - optional status for detailed restriction tooltips
 */
const CalendarDayCell = ({
  day,
  pickersDayProps,
  selected,
  inRange,
  disabled,
  disabledReason = null,
  sx,
  onSelect,
  onMouseOver,
  onFocus,
  onKeyDown,
  availabilityStatus = null,
}) => {
  const reasonClass = disabled && disabledReason ? `disabledReason-${disabledReason}` : null;
  const outsideCurrentMonth = Boolean(pickersDayProps.outsideCurrentMonth);
  const dayClassName = [
    pickersDayProps.className,
    selected && 'selected',
    inRange && 'inRange',
    reasonClass,
  ]
    .filter(Boolean)
    .join(' ');

  const restrictionTooltip =
    disabled && disabledReason
      ? getDisabledDayTooltip(disabledReason, availabilityStatus)
      : '';

  const handleFocus = (event) => {
    pickersDayProps.onFocus?.(event, day);
    onFocus?.(event, day);
  };

  const handleKeyDown = (event) => {
    if (onKeyDown?.(event, day)) {
      return;
    }
    pickersDayProps.onKeyDown?.(event, day);
  };

  const dayNode = (
    <CalendarDayRoot className={inRange ? 'inRange' : undefined} sx={sx?.root}>
      <CalendarDayButton
        {...pickersDayProps}
        className={dayClassName}
        day={day}
        selected={selected}
        disabled={disabled}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        onClick={() => !disabled && onSelect(day)}
        onMouseEnter={() => {
          if (outsideCurrentMonth) return;
          onMouseOver(day);
        }}
        {...(!outsideCurrentMonth
          ? { 'data-calendar-day': dateToIsoKey(day) }
          : {})}
        sx={sx?.day}
      />
    </CalendarDayRoot>
  );

  if (!restrictionTooltip) {
    return dayNode;
  }

  return (
    <Tooltip title={restrictionTooltip} enterDelay={300} describeChild>
      <Box component="span" sx={{ display: 'inline-flex' }}>
        {dayNode}
      </Box>
    </Tooltip>
  );
};

CalendarDayCell.propTypes = {
  day: PropTypes.instanceOf(Date).isRequired,
  pickersDayProps: PropTypes.object.isRequired,
  selected: PropTypes.bool.isRequired,
  inRange: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  disabledReason: PropTypes.oneOf([
    ...Object.values(DATE_UNAVAILABILITY_REASON),
    null,
  ]),
  sx: PropTypes.shape({
    root: PropTypes.object,
    day: PropTypes.object,
  }),
  onSelect: PropTypes.func.isRequired,
  onMouseOver: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  onKeyDown: PropTypes.func,
  availabilityStatus: PropTypes.oneOfType([
    dateAvailabilityStatusPropType,
    PropTypes.shape({ suppressTooltip: PropTypes.bool }),
  ]),
};

export default CalendarDayCell;
